/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Hightouch Events", description = "An overview of Hightouch Events.", lastUpdatedDate = new Date(1740355200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Hightouch Events",
        description: "An overview of Hightouch Events.",
        lastUpdatedDate: "Mon Feb 24 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Hightouch overview",
          "depth": 0,
          "id": "hightouch-overview"
        }, {
          "value": "Features",
          "depth": 1,
          "id": "features"
        }, {
          "value": "Benefits",
          "depth": 1,
          "id": "benefits"
        }, {
          "value": "Use cases",
          "depth": 1,
          "id": "use-cases"
        }, {
          "value": "Implementing event collection",
          "depth": 1,
          "id": "implementing-event-collection"
        }, {
          "value": "Defining your events",
          "depth": 1,
          "id": "defining-your-events"
        }, {
          "value": "Loading events into the warehouse",
          "depth": 1,
          "id": "loading-events-into-the-warehouse"
        }, {
          "value": "Event Sources and Event Destinations",
          "depth": 1,
          "id": "event-sources-and-event-destinations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li",
      img: "img",
      strong: "strong",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "hightouch-overview",
        children: "Hightouch overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch enables you to collect all the valuable behavioral event data across your customer experiences, like your website and mobile applications. It is an easy-to-use, fully customizable alternative to traditional event collection solutions and purpose-built to centralize your data in the warehouse."
      }), "\n", _jsx(_components.h3, {
        id: "features",
        children: "Features"
      }), "\n", _jsx(_components.p, {
        children: "With Hightouch, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Collect event data as it occurs across your various digital properties."
        }), "\n", _jsx(_components.li, {
          children: "Govern each event to ensure cleanliness/compliance."
        }), "\n", _jsx(_components.li, {
          children: "Load that data efficiently into your cloud data warehouse of choice, where it is then available for analytics and activation use cases."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/events-architecture.png",
          alt: "Sample error payload screenshot"
        })
      }), "\n", _jsx(_components.h3, {
        id: "benefits",
        children: "Benefits"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schema flexibility"
          }), ": Hightouch Events gives you complete control of your data schema so you can structure your data however you like."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Composability"
          }), ": Hightouch Events is fully composable, meaning that it runs on your data infrastructure, is schema-agnostic, and is modular and interoperable."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Usability"
          }), ": The tracking API is simple yet powerful, letting you concentrate on what you want to track without worrying about the nitty-gritty of how it'll end up in the warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Fair pricing"
          }), ": Priced based on event count, not monthly tracked users (MTUs)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ease of implementation"
          }), ": Backward compatibility with CDPs like Segment and RudderStack."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enterprise readiness"
          }), ": Packed with features that allow data governance, version control, and full observability."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsx(_components.p, {
        children: "With behavioral events collected and stored in the data warehouse, teams can unlock a number of different use cases:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Analytics"
          }), ": Collect data for a variety of analytics purposes, such as tracking customer behavior, measuring the performance of marketing campaigns, and identifying areas for improvement."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Marketing personalization"
          }), ": Collect and store data in a centralized repository used to create unified customer profiles and power personalized marketing campaigns."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Operational workflows"
          }), ": Capture events that trigger operational workflows, such as sending personalized emails to customers or updating CRM records."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Machine learning"
          }), ": Collect data for machine learning models, such as predicting customer churn, recommending products, and detecting fraud."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "implementing-event-collection",
        children: "Implementing event collection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch provides several SDKs (software development kits) that you can deploy to your app or site in order to start collecting events. There are separate SDKs for:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/browser",
            children: "Browser (Javascript)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/nodejs",
            children: "Node.js"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/ruby",
            children: "Ruby"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/go",
            children: "Go"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/php",
            children: "PHP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/java",
            children: "Java"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/python",
            children: "Python"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/ios",
            children: "iOS"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "./sdks/android",
            children: "Android"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Additionally, you can collect events directly from ", _jsx(_components.a, {
          href: "./sources/kafka",
          children: "Kafka"
        }), ", ", _jsx(_components.a, {
          href: "./sources/google-pubsub",
          children: "PubSub"
        }), ", and ", _jsx(_components.a, {
          href: "./sources/webhook",
          children: "Webhooks"
        }), " and inspect events in a web-based debugger to ensure you're capturing events from these different sources (including from SDKs) correctly."]
      }), "\n", _jsx(_components.p, {
        children: "Please note that there is a 96 kilobytes size limit for each event that's collected."
      }), "\n", _jsx(_components.h3, {
        id: "defining-your-events",
        children: "Defining your events"
      }), "\n", _jsxs(_components.p, {
        children: ["You can define your own custom user events and traits to track with Hightouch Events. To learn more and see examples, view the ", _jsx(_components.a, {
          href: "./event-spec",
          children: "Event Tracking Spec docs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "loading-events-into-the-warehouse",
        children: "Loading events into the warehouse"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch loads events you collect into your warehouse. You can modify the format of this data, but we do have some defaults in place for how it will load, which you can learn about in our ", _jsx(_components.a, {
          href: "./warehouse-schema",
          children: "Schema docs"
        }), ". The ", _jsx(_components.a, {
          href: "./error-handling",
          children: "Error Handling docs"
        }), " review what to do if events fail to load into the warehouse."]
      }), "\n", _jsx(_components.h3, {
        id: "event-sources-and-event-destinations",
        children: "Event Sources and Event Destinations"
      }), "\n", _jsx(_components.p, {
        children: "Event sources and event destinations are independent from the sources and destinations set up through the Integrations section of Hightouch. For example, if you want to track events and push those events to your Snowflake data warehouse, you'll need to create a new event destination for the warehouse even if you've already set up a source using the Snowflake data warehouse. If you've set up the source using a role that has write permissions, then in most cases, you'll be able to simply copy the credentials for your warehouse source for your warehouse event destination."
      })]
    });
  }
}
export default MDXContent;
